import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useAuth } from "@/store/AuthContext";
import { useShop } from "@/store/ShopContext";

type Mode = "signin" | "signup";

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, signIn, signUp } = useAuth();
  const { showToast } = useShop();

  const redirectTo = params.get("redirect") || "/";
  const initialMode = (params.get("mode") as Mode) === "signup" ? "signup" : "signin";

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!/.+@.+\..+/.test(email)) {
      setError("Digite um e-mail válido");
      return;
    }
    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setSubmitting(true);
    const result =
      mode === "signin"
        ? await signIn(email, password)
        : await signUp(email, password, fullName);
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    if (mode === "signup") {
      showToast("Conta criada com sucesso");
    } else {
      showToast("Bem-vinda de volta");
    }
    navigate(redirectTo, { replace: true });
  };

  const switchMode = () => {
    setMode((m) => (m === "signin" ? "signup" : "signin"));
    setError(null);
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 py-10 md:py-14">
        <div className="w-full max-w-[440px]">
          <div className="text-center mb-6">
            <p className="text-[12px] uppercase tracking-[0.18em] text-foreground-600 mb-2">
              {mode === "signin" ? "Bem-vinda de volta" : "Oi, prazer em te conhecer"}
            </p>
            <h1 className="font-heading text-foreground-950 text-3xl md:text-4xl mb-2">
              {mode === "signin" ? "Entrar na Casa Flor" : "Criar sua conta"}
            </h1>
            <p className="text-[13.5px] text-foreground-700">
              {mode === "signin"
                ? "Acompanhe seus pedidos, salve favoritos e finalize compras mais rápido."
                : "Salve seus favoritos, acompanhe seus pedidos e encontre peças feitas à mão."}
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="bg-background-50 rounded-2xl border border-background-200/70 p-6 space-y-4"
          >
            {mode === "signup" && (
              <div>
                <label className="block text-[13px] font-medium text-foreground-900 mb-1.5">
                  Nome completo
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Mariana Silva"
                  className="w-full h-11 px-3.5 rounded-md bg-background-50 border border-background-300 focus:border-foreground-900 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-500"
                  autoComplete="name"
                />
              </div>
            )}

            <div>
              <label className="block text-[13px] font-medium text-foreground-900 mb-1.5">
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@email.com"
                className="w-full h-11 px-3.5 rounded-md bg-background-50 border border-background-300 focus:border-foreground-900 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-500"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="flex items-center justify-between text-[13px] font-medium text-foreground-900 mb-1.5">
                Senha
                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={() => showToast("Em breve você poderá redefinir a senha por e-mail")}
                    className="text-[12px] font-normal text-foreground-700 hover:text-foreground-950 underline underline-offset-4 cursor-pointer"
                  >
                    Esqueceu?
                  </button>
                )}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Mínimo 6 caracteres" : "••••••••"}
                  className="w-full h-11 px-3.5 pr-11 rounded-md bg-background-50 border border-background-300 focus:border-foreground-900 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-500"
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md text-foreground-600 hover:text-foreground-950 hover:bg-background-100 flex items-center justify-center cursor-pointer"
                  aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                >
                  <i className={showPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="text-[13px] text-primary-700 bg-primary-100/70 border border-primary-200 rounded-md px-3 py-2 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[15px] font-semibold transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap inline-flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {mode === "signin" ? "Entrando…" : "Criando conta…"}
                </>
              ) : (
                <>{mode === "signin" ? "Entrar" : "Criar conta"}</>
              )}
            </button>

            <p className="text-[13px] text-foreground-700 text-center pt-1">
              {mode === "signin" ? "Ainda não tem conta?" : "Já tem uma conta?"}{" "}
              <button
                type="button"
                onClick={switchMode}
                className="text-foreground-950 font-medium underline underline-offset-4 hover:text-primary-600 cursor-pointer"
              >
                {mode === "signin" ? "Criar conta" : "Entrar"}
              </button>
            </p>
          </form>

          <p className="text-[12px] text-foreground-600 text-center mt-5 leading-5">
            Ao continuar, você concorda com nossos{" "}
            <a href="#" className="underline hover:text-foreground-950 cursor-pointer">Termos</a>{" "}
            e{" "}
            <a href="#" className="underline hover:text-foreground-950 cursor-pointer">Política de Privacidade</a>.
          </p>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-[13px] text-foreground-700 hover:text-foreground-950 inline-flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar à loja
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}